import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Camera, RotateCcw, Check, Upload, Sparkles, Palette } from "lucide-react";

import { useAuth } from "../AuthContext";
import { api } from "../api";
import { t } from "../i18n";
import CropOverlay from "../components/CropOverlay";

const TYPE_CONFIG = {
  blueprint: { endpoint: "/generate/blueprint", titleKey: "blueprint", color: "sky", expectedSec: 6, crop: false },
  pencil: { endpoint: "/generate/pencil-sketch", titleKey: "pencil", color: "yellow", expectedSec: 30, crop: true },
  coloring: { endpoint: "/generate/coloring", titleKey: "coloring", color: "orange", expectedSec: 45, crop: true },
  "colour-guide": { endpoint: "/generate/colour-guide", titleKey: "colour_guide", color: "lime", expectedSec: 45, crop: true },
  story: { endpoint: "/generate/comic-story", titleKey: "story", color: "pink", expectedSec: 60, crop: false },
};

function ProcessingScreen({ lang, expectedSec }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div className="page-shell flex flex-col items-center justify-center text-center" data-testid="processing-screen">
      <motion.div
        animate={{ y: [0, -22, 0], rotate: [0, -10, 10, 0] }}
        transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
        className="w-32 h-32 rounded-full bg-gradient-to-br from-pink-400 to-yellow-300 flex items-center justify-center shadow-[0_10px_0_rgba(0,0,0,0.12)] border-4 border-white"
      >
        <Sparkles size={56} strokeWidth={2.5} className="text-white" />
      </motion.div>

      <h2 className="font-heading text-3xl font-bold mt-8 text-slate-800">{t(lang, "generating")}</h2>
      <p className="text-base text-cyan-50/95 mt-2 font-semibold drop-shadow-[0_2px_8px_rgba(15,23,42,0.45)]">{t(lang, "almost_there")}</p>

      <div className="mt-8 bg-white rounded-3xl px-8 py-4 border-4 border-slate-900/10 shadow-[0_6px_0_rgba(0,0,0,0.08)]">
        <div className="font-heading text-5xl font-bold text-white tabular-nums drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]" data-testid="countdown-timer">
          {mm}:{ss}
        </div>
        <p className="text-xs text-slate-500 font-bold mt-1">Hang tight ✨ usually ~{expectedSec}s</p>
      </div>

      <div className="mt-8 flex gap-2">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-3 h-3 rounded-full bg-pink-400"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15 }}
          />
        ))}
      </div>
    </div>
  );
}

export default function Capture() {
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.blueprint;
  const navigate = useNavigate();
  const { lang, updateBalance } = useAuth();

  // For the coloring type we need to know which color_type the user picked on
  // the Drawing page. It arrives as a `?color_type=` query param; we also
  // fall back to sessionStorage so a refresh on the capture page still works.
  const colorType = (() => {
    if (type !== "coloring") return null;
    const q = searchParams.get("color_type");
    if (q) return q;
    try { return sessionStorage.getItem("dt_coloring_style") || null; } catch (_e) { return null; }
  })();

  // Map the raw English color_type string (which is what the backend expects)
  // to its translated display label so the badge respects the current UI lang.
  const colorTypeLabelKey = {
    "Oil Pastel": "coloring_style_oil_pastel",
    "Watercolor": "coloring_style_watercolor",
    "Graphite Shade": "coloring_style_graphite_shade",
    "Color Pencil Shade": "coloring_style_color_pencil_shade",
    "Oil Paint": "coloring_style_oil_paint",
  }[colorType || ""] || null;

  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const streamRef = useRef(null);

  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [captured, setCaptured] = useState(null); // {blob, url}
  const [processing, setProcessing] = useState(false);

  // Start camera
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
          audio: false,
        });
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setCameraReady(true);
      } catch (e) {
        setCameraError(e.message || "Camera not available");
      }
    })();
    return () => {
      active = false;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const snap = () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 720;
    canvas.height = v.videoHeight || 1280;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        setCaptured({ blob, url });
        stopCamera();
      },
      "image/jpeg",
      0.92
    );
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCaptured({ blob: f, url: URL.createObjectURL(f) });
    stopCamera();
  };

  const retake = () => {
    if (captured?.url) URL.revokeObjectURL(captured.url);
    setCaptured(null);
  };

  const submitBlob = async (blob) => {
    if (!blob || processing) return;
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "capture.jpg");
      if (type === "story") {
        fd.append("language", lang);
        fd.append("country", "India");
      }
      if (type === "coloring") {
        if (!colorType) {
          toast.error(t(lang, "coloring_choose_style"));
          navigate("/drawing", { replace: true });
          return;
        }
        fd.append("color_type", colorType);
      }
      const r = await api.post(cfg.endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (typeof r.data.wallet_balance === "number") updateBalance(r.data.wallet_balance);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      try {
        sessionStorage.setItem("dt_last_result", JSON.stringify(r.data));
      } catch (_) { /* quota exceeded - ignore */ }
      navigate("/drawing/result", { replace: true });
    } catch (e) {
      const detail = e.response?.data?.detail || "Generation failed";
      toast.error(detail);
      setProcessing(false);
    }
  };

  const submit = async () => {
    if (!captured) return;
    await submitBlob(captured.blob);
  };

  if (processing) return <ProcessingScreen lang={lang} expectedSec={cfg.expectedSec} />;

  return (
    <div className="page-shell" data-testid="capture-page">
      <div className="flex items-center justify-between mb-4">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/drawing")}
          className="p-3 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
        >
          <ArrowLeft size={22} strokeWidth={2.5} className="text-slate-700" />
        </button>
        <h2 className="font-heading text-2xl font-bold text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">{t(lang, cfg.titleKey)}</h2>
        <div className="w-12" />
      </div>

      {/* Selected coloring-style badge — only rendered on the coloring flow.
          Sits above the preview/crop area and stays visible through every
          phase of capture (camera → uploaded preview → crop). Purely cosmetic;
          the actual value used at submit time is `colorType`. */}
      {type === "coloring" && colorType && (
        <div
          data-testid="coloring-style-badge"
          className="mb-3 inline-flex items-center gap-2 bg-orange-100 border-2 border-orange-200 text-orange-800 rounded-full px-4 py-1.5 shadow-[0_2px_0_rgba(0,0,0,0.06)]"
        >
          <Palette size={16} strokeWidth={2.75} className="shrink-0" />
          <span className="font-heading text-sm font-bold uppercase tracking-tight leading-none">
            {colorTypeLabelKey ? t(lang, colorTypeLabelKey) : colorType}
          </span>
        </div>
      )}

      {/* Crop-enabled types skip the preview frame & buttons and show CropOverlay directly */}
      {captured && cfg.crop ? (
        <CropOverlay
          src={captured.url}
          onConfirm={(blob) => submitBlob(blob)}
          onCancel={retake}
          confirmLabel={t(lang, "use_cropped_area")}
          cancelLabel={t(lang, "retake")}
        />
      ) : (
        <>
          <div className="relative aspect-[3/4] w-full bg-slate-900 rounded-3xl overflow-hidden border-4 border-white shadow-[0_8px_0_rgba(0,0,0,0.12)]">
            {captured ? (
              <img src={captured.url} alt="captured" className="w-full h-full object-cover" data-testid="captured-preview" />
            ) : (
              <>
                <video
                  ref={videoRef}
                  data-testid="camera-video"
                  playsInline
                  muted
                  autoPlay
                  className="w-full h-full object-cover"
                />
                <div className="pointer-events-none absolute inset-6 border-4 border-dashed border-white/70 rounded-2xl" />
                {!cameraReady && !cameraError && (
                  <div className="absolute inset-0 flex items-center justify-center text-white text-lg font-bold">
                    Starting camera...
                  </div>
                )}
                {cameraError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6 text-center">
                    <p className="font-bold">Camera unavailable</p>
                    <p className="text-sm opacity-80 mt-1">{cameraError}</p>
                    <p className="text-sm mt-3">You can upload a picture below 👇</p>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={pickFile}
        className="hidden"
        data-testid="file-input"
      />

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={pickFile}
        className="hidden"
        data-testid="camera-input"
      />

      {!captured ? (
        <div className="mt-6 flex flex-col gap-3">
          <button
            data-testid="snap-btn"
            disabled={!cameraReady}
            onClick={snap}
            className="w-full bg-pink-400 hover:bg-pink-500 disabled:bg-slate-200 disabled:text-slate-400 text-white font-heading text-xl font-bold uppercase tracking-wide py-4 rounded-full btn-squish flex items-center justify-center gap-2"
          >
            <Camera size={24} strokeWidth={2.5} /> {t(lang, "take_photo")}
          </button>
          <button
            data-testid="upload-btn"
            onClick={() => fileInputRef.current?.click()}
            className="w-full bg-white border-2 border-slate-900/10 text-slate-700 font-bold py-3 rounded-full flex items-center justify-center gap-2 btn-squish"
          >
            <Upload size={20} strokeWidth={2.5} /> {t(lang, "upload_from_gallery")}
          </button>
        </div>
      ) : cfg.crop ? null : (
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            data-testid="retake-btn"
            onClick={retake}
            className="bg-white border-2 border-slate-900/10 text-slate-700 font-heading font-bold uppercase py-4 rounded-full btn-squish flex items-center justify-center gap-2"
          >
            <RotateCcw size={20} strokeWidth={2.5} /> {t(lang, "retake")}
          </button>
          <button
            data-testid="use-photo-btn"
            onClick={submit}
            className="bg-emerald-400 hover:bg-emerald-500 text-white font-heading font-bold uppercase py-4 rounded-full btn-squish flex items-center justify-center gap-2"
          >
            <Check size={22} strokeWidth={3} /> {t(lang, "use_photo")}
          </button>
        </div>
      )}
    </div>
  );
}
